'use client';

import { Box, Container, Skeleton, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { ParsePillHeading } from '@/components/ParsePillHeading';
import { StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  applyAprOverride,
  ExtendedLenderTier,
  filterAndSortTiers,
  FilterParameters,
} from '@/lib/loanFilteringHelper';
import { ComponentLoanLoanHighlight, Lender, LenderTier } from '@/types';

type Props = {
  block: ComponentLoanLoanHighlight;
};

// exclude any tiers without a rate
const filterExcludeNoRate = (tier: ExtendedLenderTier) => !tier.hideApr && tier.minApr;

export default function LoanHighlight({ block }: Props) {
  const theme = useTheme();
  const {
    LoanHighlightTitle,
    LoanHighlightDescription,
    LenderTiers: manualLenderTiers,
    HighlightDefaultPurpose,
    HighlightDefaultProductType,
    HighlightDefaultLoanAmount,
  } = block;

  const [lenders, setLenders] = useState<Lender[]>([]);
  const [lenderTiers, setLenderTiers] = useState<ExtendedLenderTier[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch lenders (same as LoanComparison)
  useEffect(() => {
    // If manual lender tiers are provided, use those
    if (manualLenderTiers && manualLenderTiers.length > 0) {
      setLenderTiers(
        manualLenderTiers
          .flatMap((tier): ExtendedLenderTier[] => {
            if (!tier) return [];
            return [
              applyAprOverride({
                ...tier,
                lenderName: tier.lender?.lenderName,
                lenderLogo: tier.lender?.lenderLogo,
                hideApr: Boolean(tier.lender?.hideApr || tier.hideApr),
              }),
            ];
          })
          .filter(filterExcludeNoRate)
      );
      setLenders([]);
      setLoading(false);
      return;
    }

    // Otherwise fetch from API
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/lenders?populate[lenderTiers][populate]=*`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
            },
          }
        );

        const json = await res.json();
        const fetchedLenders: Lender[] = Array.isArray(json) ? json : (json?.data ?? []);

        setLenders(fetchedLenders);
      } catch (err) {
        console.error('❌ Failed to fetch lenders:', err);
        setLenders([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [manualLenderTiers]);

  // Build allTiers from lender tiers (not lender-level data)
  const allTiers = useMemo(() => {
    if (lenders.length === 0) return [];

    // Use tier-level data
    return lenders.flatMap((l) =>
      (l.lenderTiers || [])
        .filter((t): t is LenderTier => Boolean(t?.documentId))
        .map((t) =>
          applyAprOverride({
            ...t,
            documentId: t.documentId ?? '',
            lenderName: l.lenderName ?? '',
            lenderLogo: l.lenderLogo ?? '',
            hideApr: Boolean(l.hideApr || t.hideApr),
          })
        )
    );
  }, [lenders]);

  // Filter and sort to get top 5 (same logic as LoanComparison)
  useEffect(() => {
    if (allTiers.length === 0) return;

    const filters: FilterParameters = {
      purposes: HighlightDefaultPurpose ? [HighlightDefaultPurpose] : undefined,
      productTypes: HighlightDefaultProductType ? [HighlightDefaultProductType] : undefined,
      loanAmount: HighlightDefaultLoanAmount ? [HighlightDefaultLoanAmount, null] : undefined,
    };

    const sorted = filterAndSortTiers(allTiers, filters, 'interestLowToHigh');
    const seen = new Set<string>();
    const deduped = sorted
      .filter((tier) => {
        const key = tier.lenderName || tier.lenderLogo || '';
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .filter(filterExcludeNoRate)
      .slice(0, 5);
    setLenderTiers(deduped);
  }, [allTiers, HighlightDefaultPurpose, HighlightDefaultProductType, HighlightDefaultLoanAmount]);

  const hasTiers = lenderTiers && lenderTiers.length > 0;
  if (!hasTiers && !loading) return null;

  return (
    <CustomBackground
      backgroundOptions={{
        BackgroundColor: block.LoanHighlightBackground?.BackgroundColor ?? 'none',
        BackgroundOrbs: block.LoanHighlightBackground?.BackgroundOrbs ?? 'none',
      }}
      displayOptions={
        {
          PaddingTop: block?.LoanHighlightDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: block?.LoanHighlightDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: block?.LoanHighlightDisplayOptions?.PaddingInline ?? 'xs',
        } as StrapiDisplayOptions
      }
      boxProps={{
        sx: {
          overflow: 'hidden',
        },
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 5, px: 2 }}>
          {LoanHighlightTitle && <ParsePillHeading title={LoanHighlightTitle} />}
          {LoanHighlightDescription && (
            <Box sx={{ mt: 2, mx: 'auto' }}>
              <GlobalBlockRenderer content={LoanHighlightDescription} />
            </Box>
          )}
        </Box>
      </Container>

      {/* Full-width scroller - only show when we have tiers or are loading */}
      {(hasTiers || loading) && (
        <Box
          sx={{
            width: '100vw', // full screen width
            position: 'relative',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: { xs: 'flex-start', sm: 'center' },
              alignItems: 'stretch',
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              gap: { xs: 2.5, sm: 4 },
              px: { xs: 3, sm: 6 },
              overflowX: { xs: 'auto', sm: 'visible' },
              overflowY: 'hidden',
              scrollSnapType: { xs: 'x mandatory', sm: 'none' },
              scrollPadding: { xs: theme.spacing(3), sm: 0 },
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': { display: 'none' },
              '-ms-overflow-style': 'none',
              'scrollbar-width': 'none',
            }}
          >
            {loading
              ? Array.from({ length: 5 }).map((_, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      flex: { xs: '0 0 80%', sm: '0 0 auto' },
                      width: { sm: 220 },
                      height: 220,
                      borderRadius: 4,
                      p: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      scrollSnapAlign: 'center',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.02), 0 8px 16px rgba(0,0,0,0.03)',
                      backgroundColor: theme.palette.common.white,
                    }}
                  >
                    <Skeleton variant="circular" width={80} height={80} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width={40} height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width={90} height={36} />
                  </Box>
                ))
              : lenderTiers.map((tier, idx) => {
                  if (!tier || tier.minApr == null) return null;
                  const { hideApr, minApr } = tier;

                  const lenderName = tier.lenderName || '';
                  const lenderLogo = tier.lenderLogo || '';

                  return (
                    <Box
                      key={idx}
                      sx={{
                        flex: { xs: '0 0 80%', sm: '0 0 auto' },
                        width: { sm: 220 },
                        height: 220,
                        borderRadius: 4,
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: theme.palette.common.white,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        scrollSnapAlign: 'center',
                        transition:
                          'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.02), 0 8px 16px rgba(0,0,0,0.03)',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          boxShadow: '0 6px 18px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.06)',
                        },
                      }}
                    >
                      {lenderLogo && (
                        <Box
                          component="img"
                          src={lenderLogo}
                          alt={`${lenderName} lender highlight logo`}
                          sx={{
                            height: { xs: 120, sm: 150, md: 180 },
                            width: { xs: 120, sm: 150, md: 180 },
                            mb: 2,
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))',
                            maxWidth: '100%',
                            mx: 'auto',
                            display: 'block',
                          }}
                        />
                      )}

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          opacity: 0.75,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        From
                      </Typography>

                      <Box sx={{ mb: 0.5 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            color: theme.palette.text.primary,
                            lineHeight: 1.1,
                          }}
                        >
                          {!hideApr && minApr ? `${minApr?.toFixed(2)}%` : 'Rate on Application'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
          </Box>
        </Box>
      )}
    </CustomBackground>
  );
}
